/**
 * C10's HR routes (design §5.8).
 *
 * Declared **separately from `HR_ROUTES`** and mounted by P11, the wiring packet, in
 * `src/router/index.ts` — the same shape `AP_C3_ROUTES`, `ORG_C1_ROUTES` and
 * `FX_C1_ROUTES` already use. The separation is not cosmetic: `src/locales/en.json` is
 * merged by P11 from the module fragments, and `src/nav/keys.test.ts` fails on a route
 * naming a `titleKey` the merged catalogue does not yet hold. Routes and their strings
 * therefore land in the same commit or neither does.
 *
 * `/hr/work-permits` itself is **not** here: it already exists in `HR_ROUTES` and C10
 * re-points it at the real endpoint-backed list, so the nav link keeps working with no
 * dependency on the wiring packet at all.
 *
 * Every route names the permission the server guards its **primary read** with, never
 * the write — a screen gated on `hr.exit.initiate` would hide the exits board from the
 * asset and IT owners who sign its clearance items off.
 */

import type { RouteRecordRaw } from 'vue-router'

import { HR_C10_PERMISSIONS } from '@/modules/hr/permissionsC10'

export const HR_C10_ROUTES: RouteRecordRaw[] = [
  {
    path: '/hr/work-permits/:id(\\d+)',
    name: 'hr-work-permit',
    component: () => import('@/modules/hr/WorkPermitView.vue'),
    meta: {
      titleKey: 'hr.routeTitles.workPermit',
      permission: HR_C10_PERMISSIONS.permitRead,
    },
  },
  {
    path: '/hr/permit-quotas',
    name: 'hr-permit-quotas',
    component: () => import('@/modules/hr/PermitQuotasView.vue'),
    meta: {
      titleKey: 'hr.routeTitles.permitQuotas',
      permission: HR_C10_PERMISSIONS.permitRead,
    },
  },
  {
    path: '/hr/leave/blackouts',
    name: 'hr-leave-blackouts',
    component: () => import('@/modules/hr/LeaveBlackoutsView.vue'),
    meta: {
      titleKey: 'hr.routeTitles.leaveBlackouts',
      permission: HR_C10_PERMISSIONS.leaveBlackoutManage,
    },
  },
  {
    path: '/hr/exits',
    name: 'hr-exits',
    component: () => import('@/modules/hr/ExitsBoardView.vue'),
    meta: { titleKey: 'hr.routeTitles.exits', permission: HR_C10_PERMISSIONS.exitRead },
  },
  {
    path: '/hr/exits/:id(\\d+)',
    name: 'hr-exit',
    component: () => import('@/modules/hr/ExitView.vue'),
    meta: { titleKey: 'hr.routeTitles.exit', permission: HR_C10_PERMISSIONS.exitRead },
  },
]
